import { ChangeDetectionStrategy, Component, computed, inject, input, output, resource, signal } from '@angular/core';
import { VerwaltungsView, WartungsZählerZuordnung } from '../wartung.types';
import { firstValueFrom, merge, of, Subject, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ToastrService } from 'ngx-toastr';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ZType, ZählerMode } from '../../zaehlerverwaltung/zaehler.enums';
import { ZählerName } from '../../zaehlerverwaltung/zaehler.types';
import { PreviewThumbnailsComponent } from '../../../_shared/preview-thumbnails/preview-thumbnails.component';
import { HilfeBeschreibungModalComponent } from '../../../_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { TransformColNamesPipe } from '../../zaehlerverwaltung/transform-col-names.pipe';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { TextBoolDgCell } from '../../../_shared/datagrid-cell/text-bool-dg-cell';
import { APIService } from 'src/app/services/APIService/api.service';

const DELAY_TIME = 500;

@Component({
  selector: 'app-wartungszaehlerzuordnung',
  imports: [
    CommonModule,
    ClarityModule,
    MrTranslatePipe,
    PreviewThumbnailsComponent,
    HilfeBeschreibungModalComponent,
    TransformColNamesPipe,
    TextBoolDgCell,
  ],
  templateUrl: './wartungszaehlerzuordnung.component.html',
  styleUrl: './wartungszaehlerzuordnung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungsZaehlerZuordnungComponent {
  private readonly zaehlerAPI = inject(ZaehlerApiService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly api = inject(WartungApiService);
  private readonly sharedAPI = inject(APIService);
  private readonly overlayService = inject(OverlayService);

  readonly otypid = input.required<number>();
  readonly viewOutput = output<VerwaltungsView>({ alias: 'view' });

  readonly #otypid$ = toObservable(this.otypid);
  readonly #maskeRefresh$ = new Subject<number>();
  readonly #zaehlerRefresh$ = new Subject<number>();

  readonly #masken$ = merge(this.#otypid$, this.#maskeRefresh$).pipe(
    switchMap(otypid => (otypid ? this.api.getZaehlerZuordnungen(otypid) : of(null)))
  );
  protected readonly maskenTable = toSignal(this.#masken$);
  protected readonly maskeDetail = signal<ZählerName>(null);
  readonly #maskeItems$ = toObservable(this.maskeDetail).pipe(
    switchMap(maske => (maske?.ID ? this.api.getWartungsMaskeList(maske.ID) : of(null)))
  );
  protected readonly maskeItems = toSignal(this.#maskeItems$);
  protected readonly selectedMasken = signal<WartungsZählerZuordnung[]>([]);

  readonly #zaehlerRs = this.zaehlerAPI.getZaehlerNames(this.otypid, () => ZählerMode.ZÄHLER);
  protected readonly zaehlerTable = this.#zaehlerRs.value;

  protected readonly zaehlerDetail = signal<ZählerName>(null);
  protected readonly skizzeRs = resource({
    params: () => this.zaehlerDetail()?.Bild || undefined,
    loader: ({ params: name }) => this.sharedAPI.getSkizzeByName(name),
  });

  readonly #zaehlerWerteRs = this.zaehlerAPI.getZaehlerWerteBySZAEHNID(() => this.zaehlerDetail()?.ID);
  protected readonly zaehlerWerte = this.#zaehlerWerteRs.value;

  protected readonly tolOptions = this.zaehlerAPI.getToleranzenBezeichnungen();
  protected readonly selectedZaehler = signal<ZählerName>(null);
  protected readonly isHelperModalOpen = signal(false);

  protected saveState = signal(ClrLoadingState.DEFAULT);

  protected readonly alert = computed<VerwaltungsView | 'zähler'>(() =>
    this.maskenTable()?.rows?.length === 0 ? 'masken'
    : this.zaehlerTable()?.rows?.length == 0 ? 'zähler'
    : null
  );
  protected readonly ZType_Header = ZType.Header;

  protected async editZuordnung(szaehnid: number | null) {
    setTimeout(() => this.saveState.set(ClrLoadingState.LOADING));
    const IDs = this.selectedMasken().map(maske => maske.ID);

    try {
      const { success, error } = await firstValueFrom(this.api.setZaehlerZuordnungen(szaehnid, IDs));

      if (success) {
        this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
        setTimeout(() => this.saveState.set(ClrLoadingState.SUCCESS), DELAY_TIME);
        setTimeout(() => this.saveState.set(ClrLoadingState.DEFAULT), DELAY_TIME * 3);
        this.#maskeRefresh$.next(this.otypid());
      } else {
        console.log('Zuordnung fehlgeschlagen:', error);
        this.toastr.error(this.mrTranslate.transform('Etwas ist schief gelaufen'));
        throw '';
      }
    } catch (_) {
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  protected openVerwaltung() {
    this.overlayService.setOverlay({
      overlay: 'zaehlerverwaltung',
      mode: ZählerMode.ZÄHLER,
      otypid: this.otypid(),
      isTopOverlay: true,
      recallData: { otypid: this.otypid(), openView: 'zähler' },
      dataRefresh: this.#zaehlerRefresh$,
    });
  }
}
